import { buildErrorEnvelope, DictErrorCode } from '@sprytki-frontend-apps/common';
import { ITokenResponseData } from 'api/interfaces/auth';
import { APP_CONFIG } from 'api/interfaces/config';
import axios, { AxiosError } from 'axios';
import { createBrowserHistory } from 'history';
import { routePath } from 'rooting/routes';
import { clearStorage, getUserDataFromAccessToken } from 'shared';
import { storageKeys } from 'shared/constants';
import { userStore } from 'stores/userStore/userStore';
import { ApiService } from './apiClientService';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { AppUpdate, AppUpdateAvailability, AppUpdateResultCode } from '@capawesome/capacitor-app-update';
import { modalStore } from 'stores/modalStore/modalStore';
const getBasename = (path: string) => path.substr(0, path.indexOf('/'));

const history = createBrowserHistory({
	basename: getBasename(window.location.pathname),
});

const baseURL = APP_CONFIG?.adminApiURL;
export const apiClient = axios.create({
	baseURL,
	timeout: 100000,
});

apiClient.interceptors.request.use(
	async (config) => {
		const token =
			sessionStorage.getItem(storageKeys.ACCESS_TOKEN) || localStorage.getItem(storageKeys.ACCESS_TOKEN);
		if (token) {
			config.headers['Authorization'] = `Bearer ${token}`;
		}
		config.headers['ClientTimeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
		config.headers['BrowserLanguage'] = localStorage.getItem(storageKeys.I18NEXTLNG) || 'en';
		config.headers['X-Api-Version'] = '5.0';

		if (!Capacitor.isNativePlatform()) {
			config.headers['APK_VERSION'] = getUserDataFromAccessToken()?.apkVersion;
		}

		if (Capacitor.isNativePlatform()) {
			try {
				const DeviceInfo = await Device.getInfo();
				const DeviceId = await Device.getId();

				config.headers!['X-App-Version'] = '4.66.0';
				config.headers!['X-Client-Platform'] = DeviceInfo.platform;
				config.headers!['X-Client-OSVersion'] = DeviceInfo.osVersion;
				config.headers!['X-Client-DeviceId'] = DeviceId.identifier;
			} catch (error) {
				console.error('Error fetching device info:', error);
			}
		}

		return config;
	},
	(error) => Promise.reject(error)
);

// #region response interceptors
const handleServerError = async (error: AxiosError) => {
	// Overwrite 500 response messages for consumers
	error.response!.data = buildErrorEnvelope(DictErrorCode.InternalError);

	return Promise.resolve(error.response);
};

// Rate limited - surface a dedicated code so the user is told to wait, not that something broke.
const handleTooManyRequestsError = async (error: AxiosError) => {
	error.response!.data = buildErrorEnvelope(DictErrorCode.TooManyRequests);

	return Promise.resolve(error.response);
};

const handleUpgradeRequiredError = async (error: AxiosError) => {
	if (!Capacitor.isNativePlatform()) {
		return handleWebUpgradeRequiredError(error);
	}

	return handleNativeUpgradeRequiredError();
};

const handleWebUpgradeRequiredError = async (error: AxiosError) => {
	try {
		await refreshTokenData(error);
		history.push(routePath.login);
		window.location.reload();
		return Promise.resolve();
	} catch (err) {
		return Promise.reject(err);
	}
};

const handleNativeUpgradeRequiredError = async () => {
	const showModal = () => modalStore.setShowUpgradeRequiredModal(true);

	if (Capacitor.getPlatform() === 'ios') {
		showModal();
		return;
	}

	try {
		const result = await AppUpdate.getAppUpdateInfo();
		if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE)
			throw new Error('In-app update not available');

		if (!result.immediateUpdateAllowed) throw new Error('Immediate Update not allowed');

		const updateResult = await AppUpdate.performImmediateUpdate();

		if (updateResult.code !== AppUpdateResultCode.OK)
			throw new Error(`Update failed: ${AppUpdateResultCode[updateResult.code]}`);
	} catch (error) {
		//assume that the backend is right that an update is required
		showModal();
	}
};

const handleRefreshError = (error: AxiosError) => {
	clearStorage(
		[storageKeys.ACCESS_TOKEN, storageKeys.REFRESH_TOKEN, storageKeys.USER_NAME, storageKeys.REMEMBER_ME],
		'session'
	);
	clearStorage(
		[storageKeys.ACCESS_TOKEN, storageKeys.REFRESH_TOKEN, storageKeys.USER_NAME, storageKeys.REMEMBER_ME],
		'local'
	);
	history.push(routePath.login);
	return Promise.reject(error);
};

const handleRetryRequest = (error: AxiosError) => {
	try {
		return new Promise((resolve, reject) => {
			let refreshToken =
				sessionStorage.getItem(storageKeys.REFRESH_TOKEN) || localStorage.getItem(storageKeys.REFRESH_TOKEN);

			if (refreshToken == null) {
				history.push(routePath.login);
				reject(error);
			}

			const handleRefreshTokenData = () =>
				refreshTokenData(error.response?.config).catch((error) => {
					history.push(routePath.login);
					reject(error);
				});

			if (!Capacitor.isNativePlatform()) {
				resolve(ApiService.checkApkVersion().then(handleRefreshTokenData));
			} else {
				resolve(handleRefreshTokenData());
			}
		});
	} catch (error) {
		return Promise.reject(error);
	}
};

apiClient.interceptors.response.use(
	(response) => response, // If the response is successful, just return it
	async (error: AxiosError) => {
		if (error.config?.headers['X-Skip-Response-Interceptor']) {
			return Promise.reject(error);
		}
		const status = error.response?.status;
		if (error?.config?.url === 'auth/login/refresh') {
			handleRefreshError(error);
		}
		if (error) {
			switch (status) {
				case 400:
					//resolve bad requests
					return Promise.resolve(error.response);
				case 401:
					return handleRetryRequest(error);
				case 426:
					return handleUpgradeRequiredError(error);
				case 429:
					return handleTooManyRequestsError(error);
				case 500:
					return handleServerError(error);
				default:
					//TODO code: "ERR_NETWORK"
					return Promise.reject(error);
			}
		}
	}
);
// #endregion

export const refreshTokenData = async (originalRequest?: any, withSignalRClient: boolean = false): Promise<void> => {
	let wasRememberMeOn = Boolean(localStorage.getItem(storageKeys.REMEMBER_ME));
	let userName =
		sessionStorage.getItem(storageKeys.USER_NAME) || (localStorage.getItem(storageKeys.USER_NAME) as string);
	let refreshToken =
		sessionStorage.getItem(storageKeys.REFRESH_TOKEN) ||
		(localStorage.getItem(storageKeys.REFRESH_TOKEN) as string);

	return new Promise((resolve, reject) => {
		ApiService.refresh(refreshToken, userName, withSignalRClient)
			.then((response) => {
				let tokens: ITokenResponseData = response.data;

				const storage = wasRememberMeOn ? localStorage : sessionStorage;

				storage.setItem(storageKeys.ACCESS_TOKEN, tokens.accessToken);

				if (storage.getItem(storageKeys.REFRESH_TOKEN)) {
					storage.setItem(storageKeys.REFRESH_TOKEN, tokens.refreshToken);
				}

				let userData = getUserDataFromAccessToken(tokens.accessToken);
				if (userData !== null) {
					userStore.setUserContext(tokens.accessToken);
				}

				const tokenData = getUserDataFromAccessToken(tokens.accessToken);
				userStore.setUserContext(tokenData);
				//calling original request after token refresh
				if (originalRequest) resolve(apiClient.request(originalRequest));

				resolve();
			})
			.catch((error) => {
				if (!withSignalRClient) {
					history.push(routePath.login);
					userStore.clearUserContext();
				}
				reject(error);
			});
	});
};

export const logout = () => {
	clearStorage(
		[storageKeys.ACCESS_TOKEN, storageKeys.REFRESH_TOKEN, storageKeys.USER_NAME, storageKeys.CONTRACT_BULK_ENABLED],
		'session'
	);
	clearStorage(
		[
			storageKeys.ACCESS_TOKEN,
			storageKeys.REFRESH_TOKEN,
			storageKeys.USER_NAME,
			storageKeys.CONTRACT_BULK_ENABLED,
			storageKeys.REMEMBER_ME,
		],
		'local'
	);
	userStore.clearUserContext();
	location.href = routePath.login;
};
